import {
  ACTION_ORIGIN_LABELS,
  ACTION_STATUS_LABELS,
  ACTION_STATUSES,
  ALERT_CLOSED_STATUSES,
  ALERT_STATUS_LABELS,
  ALERT_TYPE_LABELS,
  EVIDENCE_ACCEPT,
  evidenceFileProblem,
  actionNeedsOwnerAndDue,
  actionPlanInputSchema,
  actionPlanListSchema,
  actionPlanOptionsSchema,
  actionPlanSchema,
  actionPlanUpdateSchema,
  formatCurrency,
  formatDate,
  planActionInputSchema,
  planActionSchema,
  type ActionOriginTypeName,
  type ActionPlan,
  type ActionPlanInput,
  type ActionPlanOptions,
  type ActionStatusName,
  type PlanAction,
  type PlanActionInput,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  CalendarDays,
  ArchiveRestore,
  CheckCircle2,
  FolderKanban,
  KanbanSquare,
  List,
  ListChecks,
  Lock,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { ProgressBar } from '@/components/progress/ProgressBar';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import { moneyInput, moneyText } from '@/lib/money';
import { ActionBoard } from '@/features/actions/ActionBoard';
import { ActionCalendar } from '@/features/actions/ActionCalendar';
import { ActionExecution } from '@/features/actions/ActionExecution';
import { meetingLink } from '@/features/meetings/CriticalAnalysis';
import { cn } from '@/lib/cn';

type View = 'list' | 'board' | 'calendar';

const STATUS_LIGHT: Record<ActionStatusName, TrafficLight> = {
  PENDING: 'gray',
  IN_PROGRESS: 'blue',
  IN_VALIDATION: 'yellow',
  DONE: 'green',
  CANCELLED: 'gray',
};

const PAGE_SIZE = 20;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const when = (a: Pick<PlanAction, 'startDate' | 'dueDate'>) =>
  a.startDate && a.dueDate
    ? `${formatDate(a.startDate)} a ${formatDate(a.dueDate)}`
    : a.dueDate
      ? `Até ${formatDate(a.dueDate)}`
      : a.startDate
        ? `A partir de ${formatDate(a.startDate)}`
        : 'Sem prazo';

const originText = (p: ActionPlan) =>
  p.origin.initiative
    ? `${ACTION_ORIGIN_LABELS[p.origin.type]}: ${p.origin.initiative.name}${p.origin.initiative.archived ? ' (arquivada)' : ''}`
    : p.origin.alert
      ? alertOriginText(p.origin.alert)
      : p.origin.decision
        ? decisionOriginText(p.origin.decision)
        : p.origin.diagnosis
          ? diagnosisOriginText(p.origin.type, p.origin.diagnosis)
          : ACTION_ORIGIN_LABELS[p.origin.type];

/** Item de origem (diagnóstico ou matriz — DEC-062): o título; para quem não enxerga o item, só a origem. */
function diagnosisOriginText(
  type: ActionPlan['origin']['type'],
  item: NonNullable<ActionPlan['origin']['diagnosis']>,
) {
  const label = ACTION_ORIGIN_LABELS[type];
  return item.title === null ? `${label} (item fora do seu acesso)` : `${label}: ${item.title}`;
}

/** Decisão de origem: o que foi decidido e em qual reunião; para quem não enxerga a reunião, só a origem. */
function decisionOriginText(decision: NonNullable<ActionPlan['origin']['decision']>) {
  const label = ACTION_ORIGIN_LABELS.DECISION;
  if (decision.text === null || decision.meeting === null) return `${label} (reunião fora do seu acesso)`;
  return `${label}: "${decision.text}" em ${decision.meeting.title} (${formatDate(decision.meeting.startsAt)})`;
}

/** Alerta de origem: título (e o encerramento); fora do escopo de quem vê, só o tipo. */
function alertOriginText(alert: NonNullable<ActionPlan['origin']['alert']>) {
  const label = ACTION_ORIGIN_LABELS.ALERT;
  if (alert.title === null || alert.status === null) {
    return `${label}: ${ALERT_TYPE_LABELS[alert.type]} (alerta fora do seu escopo)`;
  }
  const closed = ALERT_CLOSED_STATUSES.includes(alert.status)
    ? ` (${ALERT_STATUS_LABELS[alert.status].toLowerCase()})`
    : '';
  return `${label}: ${alert.title}${closed}`;
}

/** Execução › Planos de Ação (T-091, RF-32, PRD §11.19): planos com ações 5W2H, filtros e status. */
export function PlanosAcaoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Planos de ação"
        description="O que será feito, por quê, onde, quando, por quem, como e quanto custa — a partir do que motivou cada plano."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os planos de ação pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <PlansSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

type Editing =
  { kind: 'plan'; plan: ActionPlan | null } | { kind: 'action'; plan: ActionPlan; action: PlanAction | null };

function PlansSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/action-plans`;
  const [params, setParams] = useSearchParams();
  // Visões do §11.19: lista (planos com as ações) e quadro por status (kanban — T-092b).
  // Visões do §11.19 e do RF-33: lista, quadro por status (T-092b) e calendário pelo prazo (T-093).
  const view: View =
    params.get('visao') === 'quadro' ? 'board' : params.get('visao') === 'calendario' ? 'calendar' : 'list';
  const setView = (next: View) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'board') out.set('visao', 'quadro');
        else if (next === 'calendar') out.set('visao', 'calendario');
        else out.delete('visao');
        return out;
      },
      { replace: true },
    );
  // ?q= (atalho da central de alertas para a ação ou o plano) já entra como busca.
  const [search, setSearch] = useState(() => params.get('q')?.trim().slice(0, 100) ?? '');
  const [q, setQ] = useState(() => params.get('q')?.trim().slice(0, 100) ?? '');
  const [originType, setOriginType] = useState<ActionOriginTypeName | ''>('');
  const [initiativeId, setInitiativeId] = useState(() => params.get('iniciativa') ?? '');
  const [responsibleId, setResponsibleId] = useState('');
  const [status, setStatus] = useState<ActionStatusName | ''>('');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  // ?atrasadas=1 abre já filtrado (link da Visão Geral — T-140b).
  const [late, setLate] = useState(() => params.get('atrasadas') === '1');
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<ActionPlan | null>(null);
  /** Ação aberta no "Acompanhar" (checklist, atualização e histórico — T-094a). */
  const [following, setFollowing] = useState<{ plan: ActionPlan; action: PlanAction } | null>(null);
  // Link da notificação (T-113d): ?plano=&acao= abre o "Acompanhar" da ação, onde estão os comentários.
  const [actionLink, setActionLink] = useState(() => {
    const plan = params.get('plano') ?? '';
    const action = params.get('acao') ?? '';
    return UUID.test(plan) && UUID.test(action) ? { plan, action } : null;
  });
  // Lida a ação do link, ela sai do endereço (trocar de projeto ou recarregar não reabre).
  useEffect(() => {
    if (!params.has('plano') && !params.has('acao')) return;
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('plano');
        out.delete('acao');
        return out;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const linkedPlan = useQuery({
    queryKey: ['action-plans', projectId, 'linked', actionLink?.plan],
    queryFn: ({ signal }) =>
      client.request(`${base}/${actionLink!.plan}`, { schema: actionPlanSchema, signal }),
    enabled: actionLink !== null,
    meta: { handlesError: true },
  });
  useEffect(() => {
    if (!actionLink) return;
    if (linkedPlan.data) {
      const action = linkedPlan.data.actions.find((a) => a.id === actionLink.action);
      if (action) setFollowing({ plan: linkedPlan.data, action });
      else toast.error('Ação não encontrada', { description: 'Ela pode ter sido excluída deste plano.' });
      setActionLink(null);
    } else if (linkedPlan.isError) {
      toast.error('Não foi possível abrir a ação', { description: errorMessage(linkedPlan.error) });
      setActionLink(null);
    }
  }, [actionLink, linkedPlan.data, linkedPlan.isError, linkedPlan.error]);
  const ids = {
    search: useId(),
    origin: useId(),
    initiative: useId(),
    responsible: useId(),
    status: useId(),
    from: useId(),
    to: useId(),
  };

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  // "Gerar plano de ação" a partir de um alerta (RN-13): ?alerta=ID abre o novo plano com a origem já escolhida;
  // as opções trazem esse alerta mesmo além do limite da lista.
  // Só um id válido vai para a API; outro valor cai no aviso de alerta indisponível.
  const [alertShortcut, setAlertShortcut] = useState(() => {
    const raw = params.get('alerta') ?? '';
    return UUID.test(raw) ? raw : '';
  });
  // O mesmo a partir de um item de diagnóstico (T-160a; DEC-061): ?diagnostico=ID; ou de um item da matriz de
  // priorização, com a origem "matriz" (T-161a; DEC-062): ?matriz=ID.
  const [shortcutFromMatrix] = useState(() => Boolean(params.get('matriz')));
  const [diagnosisShortcut, setDiagnosisShortcut] = useState(() => {
    const raw = params.get('diagnostico') ?? params.get('matriz') ?? '';
    return UUID.test(raw) ? raw : '';
  });
  const options = useQuery({
    queryKey: ['action-plans', projectId, 'options', alertShortcut, diagnosisShortcut],
    queryFn: ({ signal }) =>
      client.request(`${base}/options`, {
        query: {
          ...(alertShortcut ? { alertId: alertShortcut } : {}),
          ...(diagnosisShortcut ? { diagnosisItemId: diagnosisShortcut } : {}),
        },
        schema: actionPlanOptionsSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const filters = {
    q,
    originType,
    initiativeId,
    responsibleId,
    status,
    dueFrom,
    dueTo,
    late,
    archived,
    page,
  };
  const list = useQuery({
    queryKey: ['action-plans', projectId, 'list', filters],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: {
          q: q || undefined,
          originType: originType || undefined,
          initiativeId: initiativeId || undefined,
          responsibleId: responsibleId || undefined,
          status: status || undefined,
          dueFrom: dueFrom || undefined,
          dueTo: dueTo || undefined,
          late: late ? 'true' : undefined,
          archived: archived ? 'true' : undefined,
          page,
          pageSize: PAGE_SIZE,
        },
        schema: actionPlanListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
    enabled: view === 'list',
  });
  // As iniciativas leem as ações (progresso pelas ações e tratamento dos riscos): atualizam junto.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['action-plans', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['initiatives'] }),
    ]);
  const opts = options.data;
  const [shortcutPending, setShortcutPending] = useState(() =>
    Boolean(params.get('alerta') || params.get('diagnostico') || params.get('matriz')),
  );
  const shortcutAllowed = can('planos-acao', 'create');
  // Cada origem com a sua permissão: o item de diagnóstico com o Diagnóstico, o da matriz com a Matriz (DEC-062).
  const shortcutItemAllowed = can(shortcutFromMatrix ? 'priorizacao' : 'diagnostico', 'edit');
  useEffect(() => {
    // Uma vez, quando as opções chegam.
    if (!shortcutPending || !opts) return;
    setShortcutPending(false);
    const available = alertShortcut
      ? opts.alerts.some((a) => a.id === alertShortcut)
      : shortcutItemAllowed &&
        opts.diagnosisItems.some((d) => d.id === diagnosisShortcut && (!shortcutFromMatrix || d.inMatrix));
    if ((alertShortcut || diagnosisShortcut) && available && shortcutAllowed) {
      setEditing({ kind: 'plan', plan: null });
    } else if (alertShortcut || !diagnosisShortcut) {
      toast.error('Este alerta não pode originar um plano agora', {
        description: 'Ele foi encerrado, está fora do seu escopo ou o seu perfil não cria planos.',
      });
      setAlertShortcut('');
      setDiagnosisShortcut('');
    } else {
      toast.error(
        shortcutFromMatrix
          ? 'Este item da matriz não pode originar um plano agora'
          : 'Este item de diagnóstico não pode originar um plano agora',
        { description: 'Ele foi arquivado, está fora do seu escopo ou o seu perfil não cria planos.' },
      );
      setDiagnosisShortcut('');
    }
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('alerta');
        out.delete('diagnostico');
        out.delete('matriz');
        return out;
      },
      { replace: true },
    );
  }, [
    shortcutPending,
    opts,
    alertShortcut,
    diagnosisShortcut,
    shortcutFromMatrix,
    shortcutAllowed,
    shortcutItemAllowed,
    setParams,
  ]);
  const set =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setPage(1);
    };

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (p: ActionPlan) =>
      client.request(`${base}/${p.id}/${p.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: actionPlanSchema,
      }),
    onSuccess: async (p) => {
      setArchiving(null);
      toast.success(p.archivedAt ? 'Plano arquivado' : 'Plano restaurado', { description: p.name });
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });
  const changeStatus = useMutation({
    meta: { handlesError: true },
    mutationFn: ({ plan, action, next }: { plan: ActionPlan; action: PlanAction; next: ActionStatusName }) =>
      client.request(`${base}/${plan.id}/actions/${action.id}/status`, {
        method: 'POST',
        body: { status: next },
        schema: planActionSchema,
      }),
    onSuccess: async (a) => {
      toast.success(a.status === 'DONE' ? 'Ação concluída' : 'Status atualizado', {
        description: `${a.title}: ${ACTION_STATUS_LABELS[a.status]}`,
      });
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível mudar o status', { description: errorMessage(err) }),
  });

  const evidence = useMutation({
    meta: { handlesError: true },
    mutationFn: ({ plan, action, file }: { plan: ActionPlan; action: PlanAction; file: File | null }) => {
      const url = `${base}/${plan.id}/actions/${action.id}/evidence`;
      if (!file) return client.request(url, { method: 'DELETE', schema: planActionSchema });
      const form = new FormData();
      form.append('file', file);
      return client.request(url, { method: 'PUT', body: form, schema: planActionSchema });
    },
    onSuccess: async (a) => {
      toast.success(a.evidence ? 'Evidência anexada' : 'Evidência removida', { description: a.title });
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível salvar a evidência', { description: errorMessage(err) }),
  });

  async function savePlan(input: ActionPlanInput) {
    if (editing?.kind !== 'plan') return;
    const target = editing.plan;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: target
        ? {
            name: input.name,
            description: input.description,
            ownerId: input.ownerId,
            departmentId: input.departmentId,
            businessUnitId: input.businessUnitId,
          }
        : input,
      schema: actionPlanSchema,
    });
    setEditing(null);
    setAlertShortcut('');
    setDiagnosisShortcut('');
    toast.success(target ? 'Plano salvo' : 'Plano criado', { description: saved.name });
    await refresh();
  }
  async function saveAction(input: PlanActionInput) {
    if (editing?.kind !== 'action') return;
    const { plan, action } = editing;
    const saved = await client.request(
      action ? `${base}/${plan.id}/actions/${action.id}` : `${base}/${plan.id}/actions`,
      { method: action ? 'PUT' : 'POST', body: input, schema: planActionSchema },
    );
    setEditing(null);
    toast.success(action ? 'Ação salva' : 'Ação adicionada', { description: saved.title });
    await refresh();
  }

  const filtered = Boolean(
    q || originType || initiativeId || responsibleId || status || dueFrom || dueTo || late,
  );
  /** Só a iniciativa filtrada (o atalho do detalhe da iniciativa): o vazio convida a criar o plano dela. */
  const onlyInitiative =
    Boolean(initiativeId) && !(q || originType || responsibleId || status || dueFrom || dueTo || late);
  const clearFilters = () => {
    setSearch('');
    setOriginType('');
    setInitiativeId('');
    setResponsibleId('');
    setStatus('');
    setDueFrom('');
    setDueTo('');
    setLate(false);
    setPage(1);
  };
  const canCreatePlan = can('planos-acao', 'create') && !archived && Boolean(opts);

  return (
    <div className="mt-6 flex flex-col gap-5">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: os planos de ação ficam disponíveis só para consulta.
        </p>
      ) : null}
      {options.isError ? (
        <FormAlert tone="error">
          <span className="flex flex-wrap items-center gap-2">
            Não foi possível carregar as iniciativas e as pessoas (filtros e cadastro):{' '}
            {errorMessage(options.error)}
            <Button
              variant="outline"
              size="sm"
              loading={options.isFetching}
              onClick={() => void options.refetch()}
            >
              Tentar de novo
            </Button>
          </span>
        </FormAlert>
      ) : null}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Visão">
        {(
          [
            ['list', 'Lista', List],
            ['board', 'Quadro', KanbanSquare],
            ['calendar', 'Calendário', CalendarDays],
          ] as const
        ).map(([key, label, Icon]) => (
          <Button
            key={key}
            variant={view === key ? 'primary' : 'outline'}
            size="sm"
            aria-pressed={view === key}
            onClick={() => setView(key)}
          >
            <Icon aria-hidden />
            {label}
          </Button>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <label htmlFor={ids.search} className="sr-only">
              Buscar plano ou ação
            </label>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id={ids.search}
              type="search"
              placeholder="Buscar pelo plano ou pela ação"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-9"
            />
          </div>
          {can('planos-acao', 'create') && !archived ? (
            <Button
              className="h-11"
              onClick={() => setEditing({ kind: 'plan', plan: null })}
              disabled={!canCreatePlan}
            >
              <Plus aria-hidden />
              Novo plano
            </Button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Filter
            id={ids.origin}
            label="Origem"
            value={originType}
            onChange={set((v) => setOriginType(v as ActionOriginTypeName | ''))}
          >
            <option value="">Todas as origens</option>
            {opts?.filterOrigins.map((o) => (
              <option key={o} value={o}>
                {ACTION_ORIGIN_LABELS[o]}
              </option>
            ))}
          </Filter>
          <Filter id={ids.initiative} label="Iniciativa" value={initiativeId} onChange={set(setInitiativeId)}>
            <option value="">Todas as iniciativas</option>
            {initiativeId && opts && !opts.initiatives.some((i) => i.id === initiativeId) ? (
              // Atalho de uma iniciativa que não está entre as ativas (ex.: arquivada): o filtro continua visível.
              <option value={initiativeId}>
                {list.data?.items.find((p) => p.origin.initiative?.id === initiativeId)?.origin.initiative
                  ?.name ?? 'Iniciativa do atalho'}{' '}
                (fora das ativas)
              </option>
            ) : null}
            {opts?.initiatives.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.responsible}
            label="Responsável"
            value={responsibleId}
            onChange={set(setResponsibleId)}
          >
            <option value="">Todos os responsáveis</option>
            {opts?.people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Filter>
          {view !== 'board' ? (
            // No quadro, o status é a coluna.
            <Filter
              id={ids.status}
              label="Status"
              value={status}
              onChange={set((v) => setStatus(v as ActionStatusName | ''))}
            >
              <option value="">Todos os status</option>
              {ACTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ACTION_STATUS_LABELS[s]}
                </option>
              ))}
            </Filter>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          {/* No calendário, o período é o mês ou a semana à vista. */}
          <div className={cn('flex gap-3', view === 'calendar' && 'hidden')}>
            <div>
              <label htmlFor={ids.from} className="text-xs text-muted-foreground">
                Prazo de
              </label>
              <Input
                id={ids.from}
                type="date"
                value={dueFrom}
                onChange={(e) => set(setDueFrom)(e.target.value)}
                className="h-10"
              />
            </div>
            <div>
              <label htmlFor={ids.to} className="text-xs text-muted-foreground">
                Prazo até
              </label>
              <Input
                id={ids.to}
                type="date"
                value={dueTo}
                onChange={(e) => set(setDueTo)(e.target.value)}
                className="h-10"
              />
            </div>
          </div>
          <label className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="size-4 accent-(--brand)"
              checked={late}
              onChange={(e) => set(setLate)(e.target.checked)}
            />
            Só as atrasadas
          </label>
          {view === 'list' ? (
            <label className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="size-4 accent-(--brand)"
                checked={archived}
                onChange={(e) => set(setArchived)(e.target.checked)}
              />
              Ver só os planos arquivados
            </label>
          ) : null}
        </div>
      </div>

      {view === 'calendar' ? (
        <ActionCalendar
          base={base}
          projectId={projectId}
          filters={{
            q: q || undefined,
            originType: originType || undefined,
            initiativeId: initiativeId || undefined,
            responsibleId: responsibleId || undefined,
            status: status || undefined,
            late: late ? 'true' : undefined,
          }}
          canEdit={can('planos-acao', 'edit')}
          readOnly={readOnly}
          onChanged={refresh}
        />
      ) : view === 'board' ? (
        <ActionBoard
          base={base}
          projectId={projectId}
          filters={{
            q: q || undefined,
            originType: originType || undefined,
            initiativeId: initiativeId || undefined,
            responsibleId: responsibleId || undefined,
            dueFrom: dueFrom || undefined,
            dueTo: dueTo || undefined,
            late: late ? 'true' : undefined,
          }}
          canEdit={can('planos-acao', 'edit')}
          onChanged={refresh}
        />
      ) : list.isPending ? (
        <LoadingState rows={5} label="Carregando os planos de ação" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.total === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={ListChecks}
          title={
            onlyInitiative && !archived
              ? 'Nenhum plano desta iniciativa ainda'
              : filtered
                ? 'Nenhum plano ou ação encontrado'
                : archived
                  ? 'Nenhum plano arquivado'
                  : 'Nenhum plano de ação ainda'
          }
          description={
            onlyInitiative && !archived
              ? 'Desdobre a iniciativa em ações com 5W2H: o quê, por quê, onde, quando, quem, como e quanto custa.'
              : filtered
                ? 'Nenhuma ação corresponde à busca e aos filtros escolhidos.'
                : opts && opts.initiatives.length === 0
                  ? 'Os planos nascem de uma iniciativa (e, depois, de alertas, decisões, diagnóstico e matriz): cadastre as iniciativas do projeto primeiro.'
                  : 'Desdobre as iniciativas em ações com 5W2H: o quê, por quê, onde, quando, quem, como e quanto custa.'
          }
          action={
            onlyInitiative && !archived && canCreatePlan ? (
              <Button onClick={() => setEditing({ kind: 'plan', plan: null })}>
                <Plus aria-hidden />
                Novo plano
              </Button>
            ) : filtered ? (
              <Button variant="outline" onClick={clearFilters}>
                Limpar filtros
              </Button>
            ) : canCreatePlan && opts && opts.initiatives.length > 0 ? (
              <Button onClick={() => setEditing({ kind: 'plan', plan: null })}>
                <Plus aria-hidden />
                Novo plano
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-4" aria-busy={list.isFetching}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.total === 1 ? '1 plano' : `${list.data.total} planos`}
          </p>
          {list.data.items.map((p) => (
            <section
              key={p.id}
              aria-label={`Plano ${p.name}`}
              className="rounded-lg border border-border bg-surface p-4"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-bold">{p.name}</h2>
                    {p.archivedAt ? <StatusBadge status="gray" label="Arquivado" /> : null}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {originText(p)} · Responsável: {p.owner?.name ?? 'sem responsável'} ·{' '}
                    {[p.department?.name, p.businessUnit?.name].filter(Boolean).join(' · ') || 'Empresa toda'}
                    {p.origin.decision?.meeting ? (
                      <>
                        {' · '}
                        <Link
                          className="font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
                          to={meetingLink(organizationId, projectId, p.origin.decision.meeting.id)}
                        >
                          Abrir a reunião
                        </Link>
                      </>
                    ) : null}
                  </p>
                  {p.description ? <p className="mt-1 text-sm">{p.description}</p> : null}
                  <p className="mt-2 text-xs text-muted-foreground">{summaryText(p)}</p>
                  {p.summary.progress !== null ? (
                    <ProgressBar
                      value={p.summary.progress}
                      label={`Progresso de ${p.name}`}
                      className="mt-2 max-w-md"
                    />
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1 sm:shrink-0">
                  {!p.archivedAt && can('planos-acao', 'create') ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing({ kind: 'action', plan: p, action: null })}
                    >
                      <Plus aria-hidden />
                      Nova ação
                    </Button>
                  ) : null}
                  {!p.archivedAt && can('planos-acao', 'edit') ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Editar o plano ${p.name}`}
                      onClick={() => setEditing({ kind: 'plan', plan: p })}
                      disabled={!opts}
                    >
                      <Pencil aria-hidden />
                    </Button>
                  ) : null}
                  {can('planos-acao', 'delete') ? (
                    p.archivedAt ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={toggleArchive.isPending}
                        onClick={() => toggleArchive.mutate(p)}
                      >
                        <ArchiveRestore aria-hidden />
                        Restaurar
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Arquivar o plano ${p.name}`}
                        onClick={() => setArchiving(p)}
                      >
                        <Archive aria-hidden />
                      </Button>
                    )
                  ) : null}
                </div>
              </div>
              {p.actions.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  {p.summary.total ? 'Nenhuma ação deste plano passa nos filtros.' : 'Nenhuma ação ainda.'}
                </p>
              ) : (
                <ul aria-label={`Ações de ${p.name}`} className="mt-3 flex flex-col gap-2">
                  {p.actions.map((a) => (
                    <ActionItem
                      key={a.id}
                      action={a}
                      canEdit={!p.archivedAt && can('planos-acao', 'edit')}
                      busy={changeStatus.isPending || evidence.isPending}
                      onStatus={(next) => changeStatus.mutate({ plan: p, action: a, next })}
                      onEvidence={(file) => evidence.mutate({ plan: p, action: a, file })}
                      onEdit={() => setEditing({ kind: 'action', plan: p, action: a })}
                      onFollow={() => setFollowing({ plan: p, action: a })}
                    />
                  ))}
                </ul>
              )}
            </section>
          ))}
          {list.data.totalPages > 1 ? (
            <nav aria-label="Paginação de planos" className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Página {list.data.page} de {list.data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((x) => x - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= list.data.totalPages}
                  onClick={() => setPage((x) => x + 1)}
                >
                  Próxima
                </Button>
              </div>
            </nav>
          ) : null}
        </div>
      )}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={
          editing?.kind === 'plan'
            ? editing.plan
              ? 'Editar plano'
              : 'Novo plano de ação'
            : editing?.action
              ? 'Editar ação'
              : 'Nova ação'
        }
        description={editing?.kind === 'action' ? `Plano: ${editing.plan.name}` : undefined}
        className="w-[min(44rem,calc(100vw-2rem))]"
      >
        {editing?.kind === 'plan' && opts ? (
          <PlanForm
            initial={editing.plan ?? undefined}
            options={opts}
            defaultInitiativeId={initiativeId || undefined}
            defaultAlertId={alertShortcut || undefined}
            defaultDiagnosisItemId={diagnosisShortcut || undefined}
            defaultDiagnosisOrigin={shortcutFromMatrix ? 'PRIORITIZATION' : 'DIAGNOSIS'}
            onSubmit={savePlan}
            onCancel={() => {
              setAlertShortcut('');
              setDiagnosisShortcut('');
              setEditing(null);
            }}
            onBusyChange={setBusy}
          />
        ) : editing?.kind === 'action' ? (
          <ActionForm
            initial={editing.action ?? undefined}
            origin={editing.plan.origin.type}
            people={opts?.people ?? []}
            onSubmit={saveAction}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={following !== null}
        onOpenChange={(open) => !open && setFollowing(null)}
        title={following ? `Acompanhar: ${following.action.title}` : 'Acompanhar'}
        description={following ? `Plano: ${following.plan.name}` : undefined}
        className="w-[min(40rem,calc(100vw-2rem))]"
      >
        {following ? (
          <ActionExecution
            key={following.action.id}
            actionUrl={`${base}/${following.plan.id}/actions/${following.action.id}`}
            organizationId={organizationId}
            projectId={projectId}
            action={following.action}
            canEdit={!following.plan.archivedAt && can('planos-acao', 'edit')}
            onChanged={refresh}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar o plano?"
        description={`${archiving?.name ?? ''} sai das listas com as ações. O histórico fica guardado, e você pode restaurá-lo depois.`}
        confirmLabel="Arquivar plano"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </div>
  );
}

function summaryText(p: ActionPlan) {
  const s = p.summary;
  if (s.total === 0) return 'Sem ações';
  return [
    s.total === 1 ? '1 ação' : `${s.total} ações`,
    s.done === 1 ? '1 concluída' : `${s.done} concluídas`,
    ...(s.cancelled ? [s.cancelled === 1 ? '1 cancelada' : `${s.cancelled} canceladas`] : []),
    ...(s.late ? [s.late === 1 ? '1 atrasada' : `${s.late} atrasadas`] : []),
  ].join(' · ');
}

/** Ação com o 5W2H, o status (mudança rápida) e o atraso. */
function ActionItem({
  action: a,
  canEdit,
  busy,
  onStatus,
  onEdit,
  onEvidence,
  onFollow,
}: {
  action: PlanAction;
  canEdit: boolean;
  busy: boolean;
  onStatus: (next: ActionStatusName) => void;
  onEdit: () => void;
  /** Abre o acompanhamento: checklist, atualização e histórico (T-094a). */
  onFollow: () => void;
  /** Anexar ou trocar (arquivo) e remover (null) a evidência de execução. */
  onEvidence: (file: File | null) => void;
}) {
  const fileId = useId();
  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold">{a.title}</p>
            <StatusBadge status={STATUS_LIGHT[a.status]} label={ACTION_STATUS_LABELS[a.status]} />
            {a.late ? <StatusBadge status="red" label="Atrasada" /> : null}
          </div>
          <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            <W label="Por quê">{a.why ?? '—'}</W>
            <W label="Onde">{a.location ?? '—'}</W>
            <W label="Quando">{when(a)}</W>
            <W label="Quem">{a.responsible?.name ?? 'Sem responsável'}</W>
            <W label="Como">{a.how ?? '—'}</W>
            <W label="Quanto">{a.cost === null ? '—' : formatCurrency(a.cost)}</W>
          </dl>
          {a.completedAt ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Concluída em {formatDate(a.completedAt.slice(0, 10))}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ProgressBar value={a.progress} label={`Progresso de ${a.title}`} className="w-full max-w-xs" />
            {a.checklist.length ? (
              <span className="text-xs text-muted-foreground">
                Checklist {a.checklist.filter((i) => i.done).length}/{a.checklist.length}
              </span>
            ) : null}
            <Button variant="outline" size="sm" onClick={onFollow} aria-label={`Acompanhar ${a.title}`}>
              <ListChecks aria-hidden />
              Acompanhar
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Paperclip className="size-3.5 text-muted-foreground" aria-hidden />
            {a.evidence ? (
              <span>
                Evidência:{' '}
                <a href={a.evidence.url} target="_blank" rel="noreferrer" className="font-medium underline">
                  {a.evidence.name}
                </a>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {a.requiresEvidence ? 'Exige evidência para concluir (ainda não anexada)' : 'Sem evidência'}
              </span>
            )}
            {canEdit ? (
              <>
                <label htmlFor={fileId} className="sr-only">
                  {a.evidence ? `Trocar a evidência de ${a.title}` : `Anexar evidência de ${a.title}`}
                </label>
                <Input
                  id={fileId}
                  type="file"
                  accept={EVIDENCE_ACCEPT}
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    // Tipo e tamanho conferidos antes de enviar (o servidor confere de novo, pelo conteúdo).
                    const problem = evidenceFileProblem(file);
                    if (problem) toast.error('Arquivo não aceito', { description: problem });
                    else onEvidence(file);
                  }}
                  className="h-8 max-w-56 py-1 text-xs"
                />
                {a.evidence ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    aria-label={`Remover a evidência de ${a.title}`}
                    onClick={() => onEvidence(null)}
                  >
                    <Trash2 aria-hidden />
                    Remover
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-1 sm:shrink-0">
            <div className="w-40">
              <NativeSelect
                aria-label={`Status de ${a.title}`}
                value={a.status}
                disabled={busy}
                onChange={(e) => onStatus(e.target.value as ActionStatusName)}
                className="h-9"
              >
                {ACTION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {ACTION_STATUS_LABELS[s]}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {a.status !== 'DONE' && a.status !== 'CANCELLED' ? (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Concluir ${a.title}`}
                disabled={busy}
                onClick={() => onStatus('DONE')}
              >
                <CheckCircle2 aria-hidden />
              </Button>
            ) : null}
            <Button variant="ghost" size="icon-sm" aria-label={`Editar ${a.title}`} onClick={onEdit}>
              <Pencil aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function W({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-1">
      <dt className="shrink-0 text-muted-foreground">{label}:</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        Filtrar por {label.toLowerCase()}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/** Plano: na criação, a origem e o vínculo; depois, nome, descrição, responsável, área e unidade. */
function PlanForm({
  initial,
  options,
  defaultInitiativeId,
  defaultAlertId,
  defaultDiagnosisItemId,
  defaultDiagnosisOrigin = 'DIAGNOSIS',
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: ActionPlan;
  options: ActionPlanOptions;
  defaultInitiativeId?: string;
  /** Alerta já escolhido ("gerar plano de ação" a partir do alerta — RN-13). */
  defaultAlertId?: string;
  /** Item de diagnóstico já escolhido ("gerar plano de ação" a partir do item — DEC-061). */
  defaultDiagnosisItemId?: string;
  /** Origem do item do atalho: o diagnóstico ou a matriz de priorização (DEC-062). */
  defaultDiagnosisOrigin?: 'DIAGNOSIS' | 'PRIORITIZATION';
  onSubmit: (input: ActionPlanInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  // Só vem escolhida pelo atalho da iniciativa: a origem não muda depois, então nada de "a primeira da lista".
  const firstInitiative = options.initiatives.find((i) => i.id === defaultInitiativeId) ?? null;
  const firstAlert = options.alerts.find((a) => a.id === defaultAlertId) ?? null;
  const firstDiagnosis = options.diagnosisItems.find((d) => d.id === defaultDiagnosisItemId) ?? null;
  const can = useCan();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [originType, setOriginType] = useState<ActionOriginTypeName>(
    initial?.origin.type ??
      (firstAlert ? 'ALERT' : firstDiagnosis ? defaultDiagnosisOrigin : (options.origins[0] ?? 'INITIATIVE')),
  );
  const [initiativeId, setInitiativeId] = useState(
    initial?.origin.initiative?.id ?? firstInitiative?.id ?? '',
  );
  const [alertId, setAlertId] = useState(firstAlert?.id ?? '');
  const [diagnosisItemId, setDiagnosisItemId] = useState(firstDiagnosis?.id ?? '');
  // Origens que se ligam a um item de diagnóstico: o próprio diagnóstico e a matriz (só itens enviados a ela).
  const fromItem = originType === 'DIAGNOSIS' || originType === 'PRIORITIZATION';
  // Cada origem só com a sua permissão (DEC-062): sem ela, a lista fica vazia e o aviso diz o motivo.
  const itemsFor = (origin: ActionOriginTypeName) =>
    origin === 'PRIORITIZATION'
      ? can('priorizacao', 'edit')
        ? options.diagnosisItems.filter((d) => d.inMatrix)
        : []
      : origin === 'DIAGNOSIS' && can('diagnostico', 'edit')
        ? options.diagnosisItems
        : [];
  const itemOptions = itemsFor(originType);
  const [ownerId, setOwnerId] = useState(initial?.owner?.id ?? '');
  // A área (e a unidade) padrão vem da origem escolhida: a iniciativa, o alerta ou o item de diagnóstico.
  const [departmentId, setDepartmentId] = useState(
    initial
      ? (initial.department?.id ?? '')
      : (firstAlert?.departmentId ?? firstDiagnosis?.departmentId ?? firstInitiative?.departmentId ?? ''),
  );
  const [businessUnitId, setBusinessUnitId] = useState(
    initial
      ? (initial.businessUnit?.id ?? '')
      : (firstAlert?.businessUnitId ?? firstDiagnosis?.businessUnitId ?? ''),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    name: useId(),
    description: useId(),
    origin: useId(),
    initiative: useId(),
    alert: useId(),
    diagnosis: useId(),
    owner: useId(),
    department: useId(),
    unit: useId(),
  };
  // Atual que saiu das opções ativas continua escolhível (salvar não troca).
  const keep = <T extends { id: string }>(
    list: T[],
    current: { id: string; name: string } | null | undefined,
  ) => (current && !list.some((x) => x.id === current.id) ? [...list, current] : list);
  const people = keep(options.people, initial?.owner);
  const departments = keep(options.departments, initial?.department);
  const units = keep(options.businessUnits, initial?.businessUnit);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const common = {
      name,
      description,
      ownerId: ownerId || null,
      departmentId: departmentId || null,
      businessUnitId: businessUnitId || null,
    };
    const parsed = initial
      ? actionPlanUpdateSchema.safeParse(common)
      : actionPlanInputSchema.safeParse({
          ...common,
          originType,
          initiativeId: originType === 'INITIATIVE' ? initiativeId || null : null,
          alertId: originType === 'ALERT' ? alertId || null : null,
          diagnosisItemId: fromItem ? diagnosisItemId || null : null,
        });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit({
        ...common,
        originType,
        initiativeId: initiativeId || null,
        alertId: alertId || null,
        diagnosisItemId: diagnosisItemId || null,
        ...parsed.data,
      });
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome do plano" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={160}
          placeholder="Ex.: Plano da expansão regional"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={2}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {initial ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Origem: </span>
          {originText(initial)}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id={ids.origin} label="Origem" error={errors.originType}>
            <NativeSelect
              {...fieldA11y(ids.origin, errors.originType)}
              value={originType}
              onChange={(e) => {
                const next = e.target.value as ActionOriginTypeName;
                setOriginType(next);
                // O item escolhido só fica se vale na nova origem (matriz: só itens dela; e a permissão de cada uma).
                if (!itemsFor(next).some((d) => d.id === diagnosisItemId)) setDiagnosisItemId('');
              }}
            >
              {options.origins.map((o) => (
                <option key={o} value={o}>
                  {ACTION_ORIGIN_LABELS[o]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          {originType === 'INITIATIVE' ? (
            <FormField id={ids.initiative} label="Iniciativa que motivou o plano" error={errors.initiativeId}>
              <NativeSelect
                {...fieldA11y(ids.initiative, errors.initiativeId)}
                value={initiativeId}
                onChange={(e) => {
                  setInitiativeId(e.target.value);
                  // A área do plano acompanha a da iniciativa escolhida (pode ser trocada depois).
                  const chosen = options.initiatives.find((i) => i.id === e.target.value);
                  if (chosen) setDepartmentId(chosen.departmentId ?? '');
                }}
              >
                <option value="">Escolha a iniciativa</option>
                {options.initiatives.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          ) : originType === 'ALERT' ? (
            <FormField
              id={ids.alert}
              label="Alerta que motivou o plano"
              error={errors.alertId}
              hint={
                options.alerts.length ? undefined : (
                  <p className="text-xs text-muted-foreground">
                    Não há alertas abertos no seu escopo. Os alertas nascem dos resultados e dos prazos das
                    ações.
                  </p>
                )
              }
            >
              <NativeSelect
                {...fieldA11y(ids.alert, errors.alertId, !options.alerts.length)}
                value={alertId}
                onChange={(e) => {
                  setAlertId(e.target.value);
                  // A área e a unidade do plano acompanham as do alerta (podem ser trocadas depois).
                  const chosen = options.alerts.find((a) => a.id === e.target.value);
                  if (chosen) {
                    setDepartmentId(chosen.departmentId ?? '');
                    setBusinessUnitId(chosen.businessUnitId ?? '');
                  }
                }}
              >
                <option value="">Escolha o alerta</option>
                {options.alerts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {ALERT_TYPE_LABELS[a.type]} · {a.title}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          ) : fromItem ? (
            <FormField
              id={ids.diagnosis}
              label={
                originType === 'PRIORITIZATION'
                  ? 'Item da matriz que motivou o plano'
                  : 'Item de diagnóstico que motivou o plano'
              }
              error={errors.diagnosisItemId}
              hint={
                itemOptions.length ? undefined : (
                  <p className="text-xs text-muted-foreground">
                    {originType === 'PRIORITIZATION'
                      ? can('priorizacao', 'edit')
                        ? 'Não há itens na matriz de priorização no seu escopo. Envie itens do Diagnóstico para a matriz.'
                        : 'Gerar plano a partir da matriz exige editar a matriz de priorização, e o seu perfil não tem essa permissão.'
                      : can('diagnostico', 'edit')
                        ? 'Não há itens de diagnóstico no seu escopo. Registre-os no Diagnóstico.'
                        : 'Gerar plano a partir de um item exige editar o diagnóstico, e o seu perfil não tem essa permissão.'}
                  </p>
                )
              }
            >
              <NativeSelect
                {...fieldA11y(ids.diagnosis, errors.diagnosisItemId, !itemOptions.length)}
                value={diagnosisItemId}
                onChange={(e) => {
                  setDiagnosisItemId(e.target.value);
                  // A área e a unidade do plano acompanham as do item (podem ser trocadas depois).
                  const chosen = options.diagnosisItems.find((d) => d.id === e.target.value);
                  if (chosen) {
                    setDepartmentId(chosen.departmentId);
                    setBusinessUnitId(chosen.businessUnitId ?? '');
                  }
                }}
              >
                <option value="">Escolha o item</option>
                {itemOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          ) : null}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField id={ids.owner} label="Responsável pelo plano" error={errors.ownerId}>
          <NativeSelect
            {...fieldA11y(ids.owner, errors.ownerId)}
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">Sem responsável</option>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.department} label="Área" error={errors.departmentId}>
          <NativeSelect
            {...fieldA11y(ids.department, errors.departmentId)}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Sem área (empresa toda)</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.unit} label="Unidade" error={errors.businessUnitId}>
          <NativeSelect
            {...fieldA11y(ids.unit, errors.businessUnitId)}
            value={businessUnitId}
            onChange={(e) => setBusinessUnitId(e.target.value)}
          >
            <option value="">Sem unidade</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar plano' : 'Criar plano'}
        </Button>
      </div>
    </form>
  );
}

/** Ação com 5W2H; responsável e prazo obrigatórios quando a origem do plano exige (RN-14). */
function ActionForm({
  initial,
  origin,
  people: options,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: PlanAction;
  origin: ActionOriginTypeName;
  people: { id: string; name: string }[];
  onSubmit: (input: PlanActionInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [why, setWhy] = useState(initial?.why ?? '');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [startDate, setStartDate] = useState(initial?.startDate ?? '');
  const [dueDate, setDueDate] = useState(initial?.dueDate ?? '');
  const [responsibleId, setResponsibleId] = useState(initial?.responsible?.id ?? '');
  const [how, setHow] = useState(initial?.how ?? '');
  const [cost, setCost] = useState(moneyText(initial?.cost));
  const [status, setStatus] = useState<ActionStatusName>(initial?.status ?? 'PENDING');
  const [requiresEvidence, setRequiresEvidence] = useState(initial?.requiresEvidence ?? false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    title: useId(),
    why: useId(),
    location: useId(),
    start: useId(),
    due: useId(),
    responsible: useId(),
    how: useId(),
    cost: useId(),
    status: useId(),
  };
  const people =
    initial?.responsible && !options.some((p) => p.id === initial.responsible!.id)
      ? [...options, initial.responsible]
      : options;
  const required = actionNeedsOwnerAndDue(origin, status);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = planActionInputSchema.safeParse({
      title,
      why,
      location,
      startDate: startDate || null,
      dueDate: dueDate || null,
      responsibleId: responsibleId || null,
      how,
      cost: moneyInput(cost),
      status,
      requiresEvidence,
    });
    const found: FieldErrors = parsed.success ? {} : zodFieldErrors(parsed.error);
    // RN-14: a origem do plano exige quem e quando (menos em ação cancelada).
    if (required && !responsibleId) found.responsibleId ??= 'Informe quem é o responsável.';
    if (required && !dueDate) found.dueDate ??= 'Informe o prazo.';
    // Concluir exige a evidência quando a ação está marcada assim (anexada na lista, depois de salvar).
    if (status === 'DONE' && requiresEvidence && !initial?.evidence) {
      found.status ??= 'Esta ação exige a evidência: salve sem concluir, anexe o arquivo e então conclua.';
    }
    if (!parsed.success || hasFieldErrors(found)) return setErrors(found);
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.title} label="O quê" error={errors.title}>
        <Input
          {...fieldA11y(ids.title, errors.title)}
          value={title}
          maxLength={300}
          placeholder="Ex.: contratar dois vendedores"
          onChange={(e) => setTitle(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.why} label="Por quê (opcional)" error={errors.why}>
        <textarea
          {...fieldA11y(ids.why, errors.why)}
          value={why}
          rows={2}
          maxLength={2000}
          onChange={(e) => setWhy(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.location} label="Onde (opcional)" error={errors.location}>
          <Input
            {...fieldA11y(ids.location, errors.location)}
            value={location}
            maxLength={300}
            onChange={(e) => setLocation(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField
          id={ids.responsible}
          label={required ? 'Quem (responsável)' : 'Quem (opcional)'}
          error={errors.responsibleId}
        >
          <NativeSelect
            {...fieldA11y(ids.responsible, errors.responsibleId)}
            value={responsibleId}
            onChange={(e) => setResponsibleId(e.target.value)}
          >
            <option value="">Sem responsável</option>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.start} label="Quando: início (opcional)" error={errors.startDate}>
          <Input
            {...fieldA11y(ids.start, errors.startDate)}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField
          id={ids.due}
          label={required ? 'Quando: prazo' : 'Quando: prazo (opcional)'}
          error={errors.dueDate}
        >
          <Input
            {...fieldA11y(ids.due, errors.dueDate)}
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.cost} label="Quanto custa (R$, opcional)" error={errors.cost}>
          <Input
            {...fieldA11y(ids.cost, errors.cost)}
            value={cost}
            inputMode="decimal"
            maxLength={20}
            placeholder="Ex.: 15.000,00"
            onChange={(e) => setCost(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.status} label="Status" error={errors.status}>
          <NativeSelect
            {...fieldA11y(ids.status, errors.status)}
            value={status}
            onChange={(e) => setStatus(e.target.value as ActionStatusName)}
          >
            {ACTION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ACTION_STATUS_LABELS[s]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-(--brand)"
          checked={requiresEvidence}
          onChange={(e) => setRequiresEvidence(e.target.checked)}
        />
        Exige evidência de execução para concluir
      </label>
      <FormField id={ids.how} label="Como (opcional)" error={errors.how}>
        <textarea
          {...fieldA11y(ids.how, errors.how)}
          value={how}
          rows={2}
          maxLength={2000}
          onChange={(e) => setHow(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar ação' : 'Adicionar ação'}
        </Button>
      </div>
    </form>
  );
}
